import React, { useState, useEffect } from 'react';
import { Link2, RefreshCw, Calendar, Clock, MapPin } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { eventsAPI } from '../../services/api';

/**
 * EventSummary Component
 *
 * Displays a summary header for an event with title, status, and actions.
 * Can receive event data directly via prop OR fetch from backend using eventId.
 * This component is independent of any game-specific information.
 *
 * Props:
 * - title: Main title (e.g., "Poker Scoring", "Cricket Match")
 * - eventId: Event ID to fetch event information (used if eventData not provided)
 * - eventData: Event data object (optional - if provided, skips API fetch)
 * - description: Optional description text below the title
 * - status: Status object { label: string, variant: 'active' | 'completed' | 'pending' } (optional)
 * - onShareLink: Optional callback for share link button
 * - onRefresh: Optional callback for refresh button
 * - refreshing: Boolean indicating if refresh is in progress
 * - showEventId: Boolean to show/hide the eventId (default: true)
 */
const EventSummary = ({
  title,
  eventId,
  eventData: eventDataProp,
  description,
  status,
  onShareLink,
  onRefresh,
  refreshing = false,
  showEventId = true
}) => {
  const { theme } = useTheme();
  const [eventData, setEventData] = useState(eventDataProp || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update local state when prop changes
  useEffect(() => {
    if (eventDataProp) {
      setEventData(eventDataProp);
    }
  }, [eventDataProp]);

  // Fetch event data when eventId changes (only if eventData not provided via prop)
  useEffect(() => {
    const fetchEventData = async () => {
      // Skip fetch if event data was provided via prop
      if (eventDataProp) {
        return;
      }

      if (!eventId) {
        setEventData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await eventsAPI.getById(eventId);
        // API returns data directly or wrapped in { success, data }
        const data = response.data || response;
        if (data && data.id) {
          setEventData(data);
        } else {
          setError('Failed to load event data');
        }
      } catch (err) {
        console.error('EventSummary: Error fetching event:', err);
        setError('Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, eventDataProp]);

  const eventName = eventData?.subject || eventData?.name;
  const displayTitle = eventName ? `${title} - ${eventName}` : title;

  // Format date for display.
  //
  // Built as UTC and read back as UTC. An event's date and time are the venue's
  // wall clock, not an instant — "1 Aug, 6pm" means 6pm at the court. Reading a
  // UTC-built date back in the viewer's own zone turned that into 2pm on 31 Jul
  // for anyone in New York, so the fixture appeared to move a day when nothing
  // about it had.
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      return date.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      });
    } catch {
      return dateStr;
    }
  };

  // Format time for display
  const formatTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return timeStr || '';
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'UTC'
      });
    } catch {
      return timeStr;
    }
  };

  // Status badge styling based on variant
  const getStatusStyle = (variant) => {
    switch (variant) {
      case 'active':
        return 'bg-green-500 text-white';
      case 'completed':
        return 'bg-gray-500 text-white';
      case 'pending':
        return 'bg-yellow-500 text-white';
      case 'cancelled':
        return 'bg-red-500 text-white';
      default:
        return 'bg-blue-500 text-white';
    }
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h2 className={`text-2xl font-bold ${theme.text}`}>
              {loading ? title : displayTitle}
            </h2>
            {status && (
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getStatusStyle(status.variant)}`}>
                {status.label}
              </span>
            )}
            {onShareLink && (
              <button
                onClick={onShareLink}
                className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors"
                title="Copy link to share"
              >
                <Link2 className="w-3 h-3" />
                <span>Share Link</span>
              </button>
            )}
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className={`p-1.5 rounded-full ${theme.cardHover} transition-colors`}
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${theme.textSecondary} ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>

          {description && (
            <p className={`text-sm ${theme.textSecondary}`}>
              {description}
            </p>
          )}

          {/* Event details from backend */}
          {eventData && (
            <div className={`flex flex-wrap items-center gap-4 mt-2 text-sm ${theme.textMuted}`}>
              {eventData.date && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(eventData.date)}</span>
                </div>
              )}
              {eventData.startTime && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>
                    {formatTime(eventData.date, eventData.startTime)}
                    {eventData.endTime && ` - ${formatTime(eventData.date, eventData.endTime)}`}
                  </span>
                </div>
              )}
              {eventData.address && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span className="line-clamp-1">{eventData.address}</span>
                </div>
              )}
            </div>
          )}

          {showEventId && eventId && (
            <p className={`text-xs ${theme.textMuted} mt-1`}>
              (eventId: {eventId})
            </p>
          )}

          {error && (
            <p className={`text-xs text-red-500 mt-1`}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventSummary;
